<template>
  <div class="row justify-content-center">
    <div class="col-md-6 col-lg-5">
      <div class="signup-card">
        <!-- Step 1: Registration Form -->
        <div v-if="step === 'form'" class="form-section">
          <h1 class="page-title">{{ $t('auth.signup.title') }}</h1>

          <p class="description">
            {{ $t('auth.signup.description') }}
          </p>

          <form @submit.prevent="goToConfirm" novalidate>
            <div class="form-group">
              <label for="name" class="form-label">{{ $t('auth.signup.name') }}</label>
              <input
                type="text"
                id="name"
                class="form-control"
                :class="{ 'is-invalid': errors.name }"
                v-model="name"
                placeholder="東川 太郎"
                :disabled="processing"
                required
              >
              <div v-if="errors.name" class="invalid-feedback">
                {{ Array.isArray(errors.name) ? errors.name[0] : errors.name }}
              </div>
            </div>

            <div class="form-group">
              <label for="email" class="form-label">{{ $t('auth.signup.email') }}</label>
              <input
                type="email"
                id="email"
                class="form-control"
                :class="{ 'is-invalid': errors.email }"
                v-model="email"
                placeholder="example@town.higashikawa.lg.jp"
                :disabled="processing"
                required
              >
              <div v-if="errors.email" class="invalid-feedback">
                {{ Array.isArray(errors.email) ? errors.email[0] : errors.email }}
              </div>
            </div>

            <div class="form-group">
              <label for="office_name" class="form-label">{{ $t('auth.signup.officeName') }}</label>
              <input
                type="text"
                id="office_name"
                class="form-control"
                :class="{ 'is-invalid': errors.office_name }"
                v-model="officeName"
                placeholder="東川振興公社"
                :disabled="processing"
                required
              >
              <div v-if="errors.office_name" class="invalid-feedback">
                {{ Array.isArray(errors.office_name) ? errors.office_name[0] : errors.office_name }}
              </div>
            </div>

            <div class="form-group">
              <label for="postal_code" class="form-label">{{ $t('auth.signup.postalCode') }}</label>
              <input
                type="text"
                id="postal_code"
                class="form-control"
                :class="{ 'is-invalid': errors.postal_code }"
                v-model="postalCode"
                placeholder="999-9999"
                :disabled="processing"
                maxlength="8"
                required
              >
              <div v-if="errors.postal_code" class="invalid-feedback">
                {{ Array.isArray(errors.postal_code) ? errors.postal_code[0] : errors.postal_code }}
              </div>
            </div>

            <div class="form-group">
              <label for="address" class="form-label">{{ $t('auth.signup.address') }}</label>
              <input
                type="text"
                id="address"
                class="form-control"
                :class="{ 'is-invalid': errors.address }"
                v-model="address"
                placeholder="北海道上川郡東川町東町1-7-4"
                :disabled="processing"
                required
              >
              <div v-if="errors.address" class="invalid-feedback">
                {{ Array.isArray(errors.address) ? errors.address[0] : errors.address }}
              </div>
            </div>

            <div class="form-group">
              <label for="phone_number" class="form-label">{{ $t('auth.signup.phoneNumber') }}</label>
              <input
                type="tel"
                id="phone_number"
                class="form-control"
                :class="{ 'is-invalid': errors.phone_number }"
                v-model="phoneNumber"
                placeholder="0166649987"
                :disabled="processing"
                maxlength="15"
                required
              >
              <div v-if="errors.phone_number" class="invalid-feedback">
                {{ Array.isArray(errors.phone_number) ? errors.phone_number[0] : errors.phone_number }}
              </div>
            </div>

            <div class="form-group">
              <label for="organization" class="form-label">{{ $t('auth.signup.organization') }}</label>
              <select
                id="organization"
                class="form-select"
                :class="{ 'is-invalid': errors.organization }"
                v-model="organization"
                :disabled="processing"
                required
              >
                <option value="">{{ $t('auth.signup.selectOrganization') }}</option>
                <option value="">{{ $t('auth.signup.organizations.noOrganization') }}</option>
                <option value="HIGASHIKAWA_TOUR_ASSOCIATE">{{ $t('auth.signup.organizations.tourAssociate') }}</option>
                <option value="HIGASHIKAWA_CHAMBER_COMMERCE">{{ $t('auth.signup.organizations.chamberCommerce') }}</option>
              </select>
              <div v-if="errors.organization" class="invalid-feedback">
                {{ Array.isArray(errors.organization) ? errors.organization[0] : errors.organization }}
              </div>
            </div>

            <div class="form-group terms-agreement">
              <div class="form-check">
                <input
                  type="checkbox"
                  id="terms_agreement"
                  class="form-check-input"
                  :class="{ 'is-invalid': errors.terms_agreement }"
                  v-model="termsAgreement"
                  :disabled="processing"
                  required
                >
                <label for="terms_agreement" class="form-check-label">
                  {{ $t('auth.signup.agreePrefix') }}<router-link to="/terms-of-use" target="_blank" class="terms-link">{{ $t('legal.titleTermsOfUse') }}</router-link>{{ $t('auth.signup.agreeConjunction') }}<router-link to="/privacy-policy" target="_blank" class="terms-link">{{ $t('legal.titlePrivacyPolicy') }}</router-link>{{ $t('auth.signup.agreeSuffix') }}
                </label>
              </div>
              <div v-if="errors.terms_agreement" class="invalid-feedback">
                {{ Array.isArray(errors.terms_agreement) ? errors.terms_agreement[0] : errors.terms_agreement }}
              </div>
            </div>

            <button
              type="submit"
              class="btn btn-primary btn-submit"
              :disabled="!name || !email || !officeName || !postalCode || !address || !phoneNumber || !termsAgreement || processing"
            >
              <span v-if="processing" class="spinner-border spinner-border-sm me-2"></span>
              {{ $t('auth.signup.submit') }}
            </button>

            <button
              type="button"
              class="btn btn-secondary btn-back"
              @click="goToLogin"
              :disabled="processing"
            >
              {{ $t('auth.signup.backToLogin') }}
            </button>
          </form>
        </div>

        <!-- Step 2: Confirmation Screen -->
        <div v-else-if="step === 'confirm'" class="confirm-section">
          <h1 class="page-title">{{ $t('auth.signup.confirmTitle') }}</h1>

          <p class="description">
            {{ $t('auth.signup.confirmDescription') }}
          </p>

          <div class="confirm-details">
            <div class="confirm-item">
              <div class="confirm-label">{{ $t('auth.signup.name') }}</div>
              <div class="confirm-value">{{ name }}</div>
            </div>

            <div class="confirm-item">
              <div class="confirm-label">{{ $t('auth.signup.email') }}</div>
              <div class="confirm-value">{{ email }}</div>
            </div>

            <div class="confirm-item">
              <div class="confirm-label">{{ $t('auth.signup.officeName') }}</div>
              <div class="confirm-value">{{ officeName }}</div>
            </div>

            <div class="confirm-item">
              <div class="confirm-label">{{ $t('auth.signup.postalCode') }}</div>
              <div class="confirm-value">{{ postalCode }}</div>
            </div>

            <div class="confirm-item">
              <div class="confirm-label">{{ $t('auth.signup.address') }}</div>
              <div class="confirm-value">{{ address }}</div>
            </div>

            <div class="confirm-item">
              <div class="confirm-label">{{ $t('auth.signup.phoneNumber') }}</div>
              <div class="confirm-value">{{ phoneNumber }}</div>
            </div>

            <div class="confirm-item">
              <div class="confirm-label">{{ $t('auth.signup.organization') }}</div>
              <div class="confirm-value">
                <span v-if="organization === 'HIGASHIKAWA_TOUR_ASSOCIATE'">{{ $t('auth.signup.organizations.tourAssociate') }}</span>
                <span v-else-if="organization === 'HIGASHIKAWA_CHAMBER_COMMERCE'">{{ $t('auth.signup.organizations.chamberCommerce') }}</span>
                <span v-else>{{ $t('auth.signup.organizations.noOrganization') }}</span>
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn btn-primary btn-submit"
            @click="submit"
            :disabled="processing"
          >
            <span v-if="processing" class="spinner-border spinner-border-sm me-2"></span>
            {{ $t('auth.signup.register') }}
          </button>

          <button
            type="button"
            class="btn btn-secondary btn-back"
            @click="goBackToForm"
            :disabled="processing"
          >
            {{ $t('auth.signup.back') }}
          </button>
        </div>

        <!-- Step 3: Completion Message -->
        <div v-else-if="step === 'complete'" class="completion-section">
          <h1 class="page-title">{{ $t('auth.signup.successTitle') }}</h1>

          <p class="description" v-html="$t('auth.signup.successMessage')"></p>

          <button
            type="button"
            class="btn btn-secondary btn-back"
            @click="goToLogin"
          >
            {{ $t('auth.signup.backToLogin') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { validateSignupUser, signupUser } from '../../../api/user/AuthApi';
import { colors } from '../../../../consts/colors.js';
import { isValidEmail, isValidPhoneNumber, isValidPostalCode } from '../../../utils/validators';

const router = useRouter();
const { t } = useI18n();

const step = ref('form'); // 'form' | 'confirm' | 'complete'
const name = ref('');
const email = ref('');
const officeName = ref('');
const postalCode = ref('');
const address = ref('');
const phoneNumber = ref('');
const organization = ref('');
const termsAgreement = ref(false);
const processing = ref(false);
const errors = ref({});

/**
 * Validations on client side
 */
const validateForm = () => {
  errors.value = {};
  let isValid = true;

  // User name validations
  if (!name.value || name.value.trim() === '') {
    errors.value.name = t('auth.signup.validation.nameRequired');
    isValid = false;
  } else if (name.value.length > 255) {
    errors.value.name = t('auth.signup.validation.nameMaxLength');
    isValid = false;
  }

  // Email address validations
  if (!email.value || email.value.trim() === '') {
    errors.value.email = t('auth.signup.validation.emailRequired');
    isValid = false;
  } else if (!isValidEmail(email.value)) {
    errors.value.email = t('auth.signup.validation.emailInvalid');
    isValid = false;
  } else if (email.value.length > 255) {
    errors.value.email = t('auth.signup.validation.emailMaxLength');
    isValid = false;
  }

  // Office name validations
  if (!officeName.value || officeName.value.trim() === '') {
    errors.value.office_name = t('auth.signup.validation.officeNameRequired');
    isValid = false;
  } else if (officeName.value.length > 255) {
    errors.value.office_name = t('auth.signup.validation.officeNameMaxLength');
    isValid = false;
  }

  // Postal code validations
  if (!postalCode.value || postalCode.value.trim() === '') {
    errors.value.postal_code = t('auth.signup.validation.postalCodeRequired');
    isValid = false;
  } else if (!isValidPostalCode(postalCode.value)) {
    errors.value.postal_code = t('auth.signup.validation.postalCodeInvalid');
    isValid = false;
  }

  // Address validations
  if (!address.value || address.value.trim() === '') {
    errors.value.address = t('auth.signup.validation.addressRequired');
    isValid = false;
  } else if (address.value.length > 255) {
    errors.value.address = t('auth.signup.validation.addressMaxLength');
    isValid = false;
  }

  // Phone number validations
  if (!phoneNumber.value || phoneNumber.value.trim() === '') {
    errors.value.phone_number = t('auth.signup.validation.phoneNumberRequired');
    isValid = false;
  } else if (!isValidPhoneNumber(phoneNumber.value)) {
    errors.value.phone_number = t('auth.signup.validation.phoneNumberInvalid');
    isValid = false;
  }

  // Organization validations (No need to validate)
  // if (!organization.value) {
  //   errors.value.organization = t('auth.signup.validation.organizationRequired');
  //   isValid = false;
  // }

  // Terms agreement validation
  if (!termsAgreement.value) {
    errors.value.terms_agreement = '利用規約とプライバシーポリシーへの同意が必要です。';
    isValid = false;
  }

  return isValid;
};

/**
 * Go to confirmation screen
 */
const goToConfirm = async () => {
  // Validations on client side
  if (!validateForm()) {
    return;
  }

  // Validations on server side (before moving to confirmation screen)
  processing.value = true;
  errors.value = {};

  try {
    await validateSignupUser(
      name.value,
      email.value,
      officeName.value,
      postalCode.value,
      address.value,
      phoneNumber.value,
      organization.value
    );

    // Move to confirmation screen if validation passes
    step.value = 'confirm';
    window.scrollTo(0, 0);
  } catch (err) {
    if (err.response && err.response.data && err.response.data.errors) {
      // Server-side validation errors
      errors.value = err.response.data.errors;
    } else {
      errors.value = { email: err.message || t('common.errorGeneral') };
    }
    window.scrollTo(0, 0);
  } finally {
    processing.value = false;
  }
};

/**
 * Go back to form screen
 */
const goBackToForm = () => {
  step.value = 'form';
  errors.value = {};
  window.scrollTo(0, 0);
};

/**
 * User Registration (submit from confirmation screen)
 */
const submit = async () => {
  processing.value = true;
  errors.value = {};

  try {
    await signupUser(
      name.value,
      email.value,
      officeName.value,
      postalCode.value,
      address.value,
      phoneNumber.value,
      organization.value
    );
    step.value = 'complete';
    window.scrollTo(0, 0);
  } catch (err) {
    if (err.response && err.response.data && err.response.data.errors) {
      // Server-side validation errors - go back to form
      errors.value = err.response.data.errors;
      step.value = 'form';
      window.scrollTo(0, 0);
    } else {
      errors.value = { email: err.message || t('common.errorGeneral') };
      step.value = 'form';
      window.scrollTo(0, 0);
    }
  } finally {
    processing.value = false;
  }
};

/**
 * Navigate to login page
 */
const goToLogin = () => {
  router.push({ name: 'login' });
};
</script>

<style lang="scss" scoped>
.signup-card {
  background-color: v-bind('colors.white');
  padding: 2.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px v-bind('colors.shadowColor');
}

.page-title {
  color: v-bind('colors.textPrimary');
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 1.5rem;
}

.description {
  color: v-bind('colors.textPrimary');
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
  margin-bottom: 2rem;

  strong {
    font-weight: 600;
  }
}

.form-group {
  margin-bottom: 1.5rem;

  .form-label {
    color: v-bind('colors.textPrimary');
    font-weight: 500;
    margin-bottom: 0.5rem;
    display: block;
  }

  .form-control,
  .form-select {
    width: 100%;
    border: 1px solid v-bind('colors.borderMediumGray');
    border-radius: 4px;
    padding: 0.75rem;
    color: v-bind('colors.textPrimary');

    &:focus {
      border-color: v-bind('colors.btnBgColorPrimary');
      box-shadow: 0 0 0 0.2rem v-bind('colors.shadowPrimary');
      outline: none;
    }

    &.is-invalid {
      border-color: v-bind('colors.error');
    }
  }

  .invalid-feedback {
    color: v-bind('colors.error');
    font-size: 0.875rem;
    margin-top: 0.25rem;
    display: block;
  }
}

.terms-agreement {
  .form-check {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .form-check-input {
    margin-top: 0.25rem;
    cursor: pointer;
    flex-shrink: 0;

    &.is-invalid {
      border-color: v-bind('colors.error');
    }
  }

  .form-check-label {
    color: v-bind('colors.textPrimary');
    font-size: 0.875rem;
    line-height: 1.5;
    cursor: pointer;
  }

  .terms-link {
    color: v-bind('colors.btnBgColorPrimary');
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.btn {
  width: 100%;
  padding: 0.75rem;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-submit {
  background-color: v-bind('colors.btnBgColorPrimary');
  border-color: v-bind('colors.btnBgColorPrimary');
  color: v-bind('colors.white');
  margin-bottom: 1rem;
  border: none;

  &:hover:not(:disabled) {
    background-color: v-bind('colors.blue[600]');
    border-color: v-bind('colors.blue[600]');
  }

  &:active:not(:disabled) {
    background-color: v-bind('colors.blue[700]');
  }
}

.btn-back {
  background-color: v-bind('colors.white');
  color: v-bind('colors.btnBgColorPrimary');
  border: 1px solid v-bind('colors.borderMediumGray');

  &:hover:not(:disabled) {
    background-color: v-bind('colors.gray[50]');
  }

  &:active:not(:disabled) {
    background-color: v-bind('colors.gray[300]');
  }
}

.confirm-details {
  margin-bottom: 2rem;
  border: 1px solid v-bind('colors.borderMediumGray');
  border-radius: 4px;
  overflow: hidden;
}

.confirm-item {
  display: flex;
  border-bottom: 1px solid v-bind('colors.borderMediumGray');

  &:last-child {
    border-bottom: none;
  }
}

.confirm-label {
  flex: 0 0 40%;
  padding: 1rem;
  background-color: v-bind('colors.gray[50]');
  color: v-bind('colors.textPrimary');
  font-weight: 500;
  border-right: 1px solid v-bind('colors.borderMediumGray');
}

.confirm-value {
  flex: 1;
  padding: 1rem;
  color: v-bind('colors.textPrimary');
  background-color: v-bind('colors.white');
  word-break: break-word;
}
</style>